import React from "react";
import { MoreVertical, Edit, Trash2, Eye } from "lucide-react";
import { DailyEntry } from "../../types/dailyEntry";
import { CustomerTypeBadge } from "../ui/CustomerTypeBadge";

interface DailyEntryTableProps {
  dailyEntries: DailyEntry[];
  onEdit?: (entry: DailyEntry) => void;
  onDelete?: (entryId: string) => void;
  onView?: (entry: DailyEntry) => void;
  isLoading?: boolean;
}

export const DailyEntryTable: React.FC<DailyEntryTableProps> = ({
  dailyEntries,
  onEdit,
  onDelete,
  onView,
  isLoading = false,
}) => {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

  const handleMenuToggle = (entryId: string) => {
    setOpenMenuId(openMenuId === entryId ? null : entryId);
  };

  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading daily entries...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg overflow-visible">
      <table className="w-full">
        <thead className="bg-slate-700">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Entry Date
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Customer
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Customer ID
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Contact
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Type
            </th>

            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700">
          {dailyEntries.map((entry) => (
            <tr key={entry.id} className="hover:bg-slate-700 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-white">
                  {formatDate(entry.entryDate)}
                </div>
                <div className="text-sm text-slate-400">
                  {formatTime(entry.entryDate)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  {entry.customer.photo ? (
                    <img
                      className="h-10 w-10 rounded-full object-cover"
                      src={`${process.env.REACT_APP_API_BASE_URL}/${entry.customer.photo}`}
                      alt={entry.customer.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}
                  {/* Fallback initials avatar */}
                  <div
                    className={`h-10 w-10 rounded-full bg-slate-600 flex items-center justify-center text-sm font-medium text-white ${entry.customer.photo ? "hidden" : ""}`}
                  >
                    {entry.customer.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-white">
                      {entry.customer.name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                {entry.customer.playerId || "—"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-white">{entry.customer.email}</div>
                <div className="text-sm text-slate-400">
                  {entry.customer.phone}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <CustomerTypeBadge type={entry.customer.customerType} />
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400 relative">
                <button
                  className="p-1 hover:bg-slate-600 rounded"
                  onClick={() => handleMenuToggle(entry.id)}
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                {/* Dropdown Menu */}
                {openMenuId === entry.id && (
                  <div className="absolute right-0 mt-2 w-48 bg-slate-700 rounded-md shadow-lg z-10 border border-slate-600">
                    <div className="py-1">
                      {onView && (
                        <button
                          className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white"
                          onClick={() => handleAction(() => onView(entry))}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </button>
                      )}
                      {onEdit && (
                        <button
                          className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white"
                          onClick={() => handleAction(() => onEdit(entry))}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Entry
                        </button>
                      )}
                      {onDelete && (
                        <button
                          className="flex items-center w-full px-4 py-2 text-sm text-red-300 hover:bg-red-600 hover:text-white"
                          onClick={() => handleAction(() => onDelete(entry.id))}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Entry
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {dailyEntries.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-slate-400">No daily entries found</div>
        </div>
      )}

      {openMenuId && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setOpenMenuId(null)}
        />
      )}
    </div>
  );
};
